/**
 * BlogPage Component
 * Main blog listing page with search, filters, and featured posts
 */
import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import BlogLayout from '../../layouts/blog/BlogLayout';
import { BlogCard, BlogSearch, CategoryFilter } from '../../components/blog';
import {
  getAllBlogs,
  getFeaturedBlogs,
  searchBlogs,
  getBlogsByCategory,
} from '../../content/blogs';

const BlogPage = () => {
  const { i18n, t } = useTranslation();
  const language = i18n.language || 'es';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get all blogs and featured blogs
  const allBlogs = useMemo(() => getAllBlogs(), []);
  const featuredBlogs = useMemo(() => getFeaturedBlogs(), []);

  // Filter blogs based on search and category
  const filteredBlogs = useMemo(() => {
    let blogs = allBlogs;

    // Filter by category
    if (selectedCategory !== 'all') {
      blogs = getBlogsByCategory(selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const searchResults = searchBlogs(searchQuery, language);
      blogs = blogs.filter((blog) =>
        searchResults.some((result) => result.id === blog.id)
      );
    }

    return blogs;
  }, [allBlogs, selectedCategory, searchQuery, language]);

  // Whether to show featured section (only when no filters applied)
  const showFeatured = !searchQuery && selectedCategory === 'all';

  // Non-featured blogs for the grid
  const gridBlogs = useMemo(() => {
    if (showFeatured) {
      return filteredBlogs.filter((blog) => !blog.featured);
    }
    return filteredBlogs;
  }, [filteredBlogs, showFeatured]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  return (
    <BlogLayout>
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12 text-center"
      >
        <h1 className="mb-4 text-4xl font-bold text-neutral-900 dark:text-white sm:text-5xl">
          {t('blog.title', 'Blog')}
        </h1>
        <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">
          {t(
            'blog.subtitle',
            'Guías, recursos y consejos para emprendedores que quieren llevar su negocio al siguiente nivel.'
          )}
        </p>

        {/* Search */}
        <div className="mx-auto max-w-md">
          <BlogSearch onSearch={handleSearch} />
        </div>
      </motion.section>

      {/* Category Filters */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8 flex justify-center"
      >
        <CategoryFilter
          selected={selectedCategory}
          onSelect={handleCategorySelect}
        />
      </motion.section>

      {/* Featured Section */}
      {showFeatured && featuredBlogs.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">
            {t('blog.featured', 'Destacados')}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featuredBlogs.map((blog, index) => (
              <BlogCard key={blog.id} blog={blog} index={index} />
            ))}
          </div>
        </motion.section>
      )}

      {/* All Posts Grid */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        {showFeatured && (
          <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">
            {t('blog.allPosts', 'Todos los artículos')}
          </h2>
        )}

        {/* Results count when filtering */}
        {(searchQuery || selectedCategory !== 'all') && (
          <p className="mb-6 text-sm text-neutral-600 dark:text-neutral-400">
            {t('blog.resultsCount', '{{count}} artículos encontrados', {
              count: filteredBlogs.length,
            })}
          </p>
        )}

        {/* Grid */}
        {gridBlogs.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gridBlogs.map((blog, index) => (
              <BlogCard
                key={blog.id}
                blog={blog}
                index={showFeatured ? index + featuredBlogs.length : index}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-neutral-200 bg-white p-12 text-center dark:border-neutral-800 dark:bg-neutral-900">
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              {t('blog.noResults', 'No se encontraron artículos.')}
            </p>
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-500">
              {t(
                'blog.tryDifferentSearch',
                'Probá con otros términos de búsqueda.'
              )}
            </p>
          </div>
        )}
      </motion.section>
    </BlogLayout>
  );
};

export default BlogPage;
